/**
 * Slow Maui Agent Portal - Router
 */
import type { AgentClient } from '@slowmaui/api-client';
import { isAdminMode } from './adminMode';

interface AgentProfile {
  id: string;
  subdomain: string;
  name: string;
  email: string;
  // Progressive-access fields (per Todd 2026-10-24) - see main.ts's
  // toAgentProfile. Only read by pages that need them (Dashboard, Pages);
  // declared here as the canonical shape passed to every render().
  status?: 'pending' | 'approved';
  billingStatus?: string;
  trialEndsAt?: string | null;
}

// Page modules (lazy loaded)
type PageModule = {
  render: (container: HTMLElement, client: AgentClient, profile: AgentProfile | null) => Promise<void>;
  cleanup?: () => void;
};

// Route definitions. `content` and `seo` branch by admin mode rather than
// being one fixed module - non-admins get the simplified AgentContent.ts
// (insights + suggestions) instead of the full editor, and Suggestions is
// entirely admin-only (its own review queue has no non-admin equivalent).
const routes: Record<string, () => Promise<PageModule>> = {
  map: () => import('./pages/AgentMap'),
  messaging: () => import('./pages/Messaging'),
  dashboard: () => import('./pages/Dashboard'),
  profile: () => import('./pages/Profile'),
  pages: () => import('./pages/Pages'),
  content: () => (isAdminMode() ? import('./pages/ContentGenerator') : import('./pages/AgentContent')),
  seo: () => import('./pages/SEOManager'),
  suggestions: () => import('./pages/Suggestions'),
  'market-activity': () => import('./pages/MarketActivity'),
  contacts: () => import('./pages/Contacts'),
  settings: () => import('./pages/Settings'),
  onboarding: () => import('./pages/Onboarding'),
};

// Current page cleanup function
let currentCleanup: (() => void) | undefined;

// Router state
let container: HTMLElement | null = null;
let client: AgentClient | null = null;
let profile: AgentProfile | null = null;

/**
 * Initialize the router
 */
function init(pageContainer: HTMLElement, agentClient: AgentClient, agentProfile: AgentProfile | null): void {
  container = pageContainer;
  client = agentClient;
  profile = agentProfile;
}

/**
 * Update profile (after onboarding)
 */
function setProfile(newProfile: AgentProfile): void {
  profile = newProfile;
}

/**
 * Parse route from hash
 */
function parseRoute(hash: string): { page: string; params: Record<string, string> } {
  // Remove leading #/
  const path = hash.replace(/^#\/?/, '');
  const parts = path.split('/');
  const page = parts[0] || 'dashboard';

  // Parse params (e.g., #/contact-detail/123)
  const params: Record<string, string> = {};
  if (parts.length > 1) {
    params.id = parts[1];
  }

  return { page, params };
}

/**
 * Navigate to a route
 */
async function navigate(hash: string): Promise<void> {
  if (!container || !client) {
    console.error('Router not initialized');
    return;
  }

  // Cleanup previous page
  if (currentCleanup) {
    currentCleanup();
    currentCleanup = undefined;
  }

  // Parse route
  const { page, params } = parseRoute(hash);

  // Update URL hash
  if (window.location.hash !== hash) {
    window.location.hash = hash;
  }

  // Dispatch route change event
  window.dispatchEvent(new CustomEvent('route-change', { detail: { page } }));

  // Show loading state
  container.innerHTML = `
    <div class="page-loading">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
  `;

  try {
    // Load page module
    const routeKey = page;
    const loader = routes[routeKey];

    if (!loader) {
      container.innerHTML = `
        <div class="page-error">
          <h2>Page Not Found</h2>
          <p>The page "${page}" does not exist.</p>
          <a href="#/dashboard" class="btn btn-primary">Go to Dashboard</a>
        </div>
      `;
      return;
    }

    const module = await loader();

    // Store cleanup function
    currentCleanup = module.cleanup;

    // Store params for the page to access
    (container as any).__routeParams = params;

    // Render page
    await module.render(container, client, profile);
  } catch (error) {
    console.error('Error loading page:', error);
    container.innerHTML = `
      <div class="page-error">
        <h2>Error Loading Page</h2>
        <p>Something went wrong. Please try again.</p>
        <button class="btn btn-primary" onclick="location.reload()">Reload</button>
      </div>
    `;
  }
}

/**
 * Get current route params
 */
function getParams(): Record<string, string> {
  if (!container) return {};
  return (container as any).__routeParams || {};
}

export const router = {
  init,
  navigate,
  getParams,
  setProfile,
};
